import React, { useEffect } from "react";

// region External libraries
import Yup from "src/libraries/yup";
import { Controller, SubmitHandler, useForm, useWatch } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { format } from "date-fns";
import { Button, useToast } from "@npm-telluria-tecnologia/telluria-ui";
// endregion External libraries
// region Helpers
import utils from "@helpers/utils";
import { handleErrorMessage } from "@helpers/handleErrorMessage";
// endregion Helpers
// region Hooks
import { useCommandMutation } from "@hooks/useCommandMutation";
// endregion Hooks
// region Languages
import useTranslation from "@languages/useTranslation";
import { AnnouncementMessages, GlobalMessages } from "@languages/interfaces";
// endregion Languages
// region Libraries
import { Checkbox, FormControlLabel, TextField } from "@libraries/mui/components";
// endregion Libraries
// region Molecules
import AnnouncementAudienceFields from "@molecules/AnnouncementAudienceFields";
import ModalFormDialog from "@molecules/ModalFormDialog";
// endregion Molecules
// region Store
import {
  AnnouncementManagementRow,
  AnnouncementTargetTenant,
  AnnouncementTargetUser,
  TAnnouncementAudience
} from "src/store/graphql/groups/announcement/query.graphql";
import {
  AnnouncementInput,
  createAnnouncementMutation,
  TCreateAnnouncementMutation,
  TUpdateAnnouncementMutation,
  updateAnnouncementMutation
} from "src/store/graphql/groups/announcement/mutation.graphql";
// endregion Store
// region Styles
import * as Styled from "./styles";
// endregion Styles

// region Interfaces
interface IFormInputs {
  title: string;
  message: string;
  link: string;
  linkLabel: string;
  startDate: string;
  endDate: string;
  highlight: boolean;
  audience: TAnnouncementAudience;
  targetTenants: AnnouncementTargetTenant[];
  targetUsers: AnnouncementTargetUser[];
}

interface FormAnnouncementProps {
  currentAnnouncement?: AnnouncementManagementRow | null;
  onClose: () => void;
  onSaved: () => void;
}
// endregion Interfaces

// region Constants
const formSchema = Yup.object().shape({
  title: Yup.string().required().max(200),
  message: Yup.string().required().max(1000),
  link: Yup.string().optional().max(2048),
  linkLabel: Yup.string().optional().max(40),
  startDate: Yup.string().required(),
  endDate: Yup.string().required()
});

// The backend stores and compares announcement dates in UTC (it reads them
// back as UTC and checks the window against UtcNow), so the datetime-local
// value (the admin's local wall time) is converted to a UTC ISO instant on
// the way out and back to local wall time on the way in.
const toBackendDate = (localValue: string) => new Date(localValue).toISOString();
const toFieldDate = (isoValue: string) => format(new Date(isoValue), "yyyy-MM-dd'T'HH:mm");

const pickCreate = (data: TCreateAnnouncementMutation) => data.announcement.createAnnouncement;
const pickUpdate = (data: TUpdateAnnouncementMutation) => data.announcement.updateAnnouncement;
// endregion Constants

/**
 * Create/edit announcement dialog (full-form replace on edit). Mounted only
 * while open. The staff gate is enforced server-side; this form assumes the
 * caller already passed the page gate.
 */
const FormAnnouncement: React.FC<FormAnnouncementProps> = ({
  currentAnnouncement,
  onClose,
  onSaved
}) => {

  // region Hooks
  const create = useCommandMutation<TCreateAnnouncementMutation>(createAnnouncementMutation, pickCreate, { onSuccess: onSaved });
  const update = useCommandMutation<TUpdateAnnouncementMutation>(updateAnnouncementMutation, pickUpdate, { onSuccess: onSaved });
  const {
    control,
    setValue,
    handleSubmit,
    formState: { errors: formErrors }
  } = useForm<IFormInputs>({
    resolver: yupResolver(formSchema),
    reValidateMode: "onChange",
    mode: "onBlur"
  });
  const { t } = useTranslation();
  const { addToast } = useToast();
  const audience = useWatch({ control, name: "audience", defaultValue: "EVERYONE" });
  // endregion Hooks

  // region Constants
  const mode = currentAnnouncement ? "EDIT" : "CREATE";
  const isLoading = create.loading || update.loading;
  // endregion Constants

  // region Handlers
  const handleSubmitForm: SubmitHandler<IFormInputs> = async (data: IFormInputs) => {
    if (new Date(data.endDate).getTime() <= new Date(data.startDate).getTime()) {
      addToast({ type: "error", title: t(AnnouncementMessages.endAfterStart) });

      return;
    }

    const tenantIds = (data.targetTenants ?? []).map((tenant) => tenant.tenantId);
    const userIds = (data.targetUsers ?? []).map((user) => user.userId);

    // The server enforces this too; checking here keeps the dialog open with the
    // pickers still filled instead of bouncing the whole form back.
    if (data.audience === "TARGETED" && tenantIds.length === 0 && userIds.length === 0) {
      addToast({ type: "error", title: t(AnnouncementMessages.audienceRequired) });

      return;
    }

    const input: AnnouncementInput = {
      title: data.title.trim(),
      message: data.message.trim(),
      link: data.link.trim() || null,
      linkLabel: data.linkLabel.trim() || null,
      startDate: toBackendDate(data.startDate),
      endDate: toBackendDate(data.endDate),
      highlight: data.highlight,
      audience: data.audience,
      tenantIds,
      userIds
    };

    if (mode === "CREATE") await create.run({ input });
    else await update.run({ id: currentAnnouncement?.id, input });
  };
  // endregion Handlers

  useEffect(() => {
    if (currentAnnouncement) {
      setValue("title", currentAnnouncement.title);
      setValue("message", currentAnnouncement.message);
      setValue("link", currentAnnouncement.link ?? "");
      setValue("linkLabel", currentAnnouncement.linkLabel ?? "");
      setValue("startDate", toFieldDate(currentAnnouncement.startDate));
      setValue("endDate", toFieldDate(currentAnnouncement.endDate));
      setValue("highlight", currentAnnouncement.highlight);
      setValue("audience", currentAnnouncement.audience);
      setValue("targetTenants", currentAnnouncement.targetTenants ?? []);
      setValue("targetUsers", currentAnnouncement.targetUsers ?? []);
    }
  }, [currentAnnouncement, setValue]);

  return (
    <ModalFormDialog
      open
      title={mode === "CREATE" ? t(AnnouncementMessages.formTitleNew) : t(AnnouncementMessages.formTitleEdit)}
      subtitle1={t(AnnouncementMessages.formSubtitle)}
      onClose={onClose}
    >
      <Styled.FormContainer
        onSubmit={(e) => utils.preventDefaultFormSubmit(e, handleSubmit(handleSubmitForm))}
      >
        <Controller
          name="title"
          control={control}
          defaultValue=""
          render={({ field }) => (
            <TextField
              {...field}
              autoComplete="off"
              label={t(AnnouncementMessages.fieldTitle)}
              variant="outlined"
              error={!!formErrors.title}
              helperText={handleErrorMessage(formErrors.title, t)}
              size="small"
              fullWidth
            />
          )}
        />
        <Controller
          name="message"
          control={control}
          defaultValue=""
          render={({ field }) => (
            <TextField
              {...field}
              autoComplete="off"
              label={t(AnnouncementMessages.fieldMessage)}
              variant="outlined"
              error={!!formErrors.message}
              helperText={handleErrorMessage(formErrors.message, t)}
              multiline
              rows={3}
              size="small"
              fullWidth
            />
          )}
        />
        <Styled.FieldRow>
          <Controller
            name="link"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextField
                {...field}
                autoComplete="off"
                label={t(AnnouncementMessages.fieldLink)}
                placeholder="https://"
                variant="outlined"
                error={!!formErrors.link}
                helperText={handleErrorMessage(formErrors.link, t)}
                size="small"
                fullWidth
              />
            )}
          />
          <Controller
            name="linkLabel"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextField
                {...field}
                autoComplete="off"
                label={t(AnnouncementMessages.fieldLinkLabel)}
                variant="outlined"
                error={!!formErrors.linkLabel}
                helperText={handleErrorMessage(formErrors.linkLabel, t)}
                size="small"
              />
            )}
          />
        </Styled.FieldRow>
        <Styled.HelperText>{t(AnnouncementMessages.linkHelper)}</Styled.HelperText>
        <Styled.DateRow>
          <Controller
            name="startDate"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextField
                {...field}
                type="datetime-local"
                label={t(AnnouncementMessages.fieldStartDate)}
                variant="outlined"
                error={!!formErrors.startDate}
                helperText={handleErrorMessage(formErrors.startDate, t)}
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            )}
          />
          <Controller
            name="endDate"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextField
                {...field}
                type="datetime-local"
                label={t(AnnouncementMessages.fieldEndDate)}
                variant="outlined"
                error={!!formErrors.endDate}
                helperText={handleErrorMessage(formErrors.endDate, t)}
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            )}
          />
        </Styled.DateRow>
        <AnnouncementAudienceFields control={control} targeted={audience === "TARGETED"} />
        <Controller
          name="highlight"
          control={control}
          defaultValue={false}
          render={({ field }) => (
            <FormControlLabel
              control={<Checkbox {...field} checked={field.value} />}
              label={t(AnnouncementMessages.fieldHighlight) as string}
            />
          )}
        />
        <Styled.FormButtons>
          <Button
            text={t(GlobalMessages.buttonCancel)}
            type="button"
            onClick={onClose}
            disabled={isLoading}
          />
          <Button
            text={t(GlobalMessages.saveText)}
            type="submit"
            className="default-submit-button"
            loading={isLoading}
          />
        </Styled.FormButtons>
      </Styled.FormContainer>
    </ModalFormDialog>
  );
};

FormAnnouncement.defaultProps = {
  currentAnnouncement: null
};

export default FormAnnouncement;
